import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { notifications } from '@mantine/notifications';
import type { ReceiptView } from 'shared-models/browser';
import { useEmployeeSession } from '../../../../../contexts/EmployeeSessionProvider';
import { printersService } from '../../../../../services/printers/printers.service';
import type { PrinterView } from '../../../../../services/printers/printers.service';
import { ordersService } from '../../../../../services/orders/orders.service';
import { receiptService } from '../../../../../services/receipt/receipt.service';
import { captureReceiptSnapshot } from '../shared/receipt.utils';
import { toastReceiptPrintResults } from './receipt-print-toast';
import { receiptsForPrinting } from './receipt-split.util';
import { resolveReceiptAutoPrinterId, resolveReceiptPrintTarget } from '../../../hooks/fixed-printer.util';

type ControllerParams = {
  opened: boolean;
  tenantId: string;
  orderId: number;
  paymentlineId?: number;
  onClose: () => void;
  onHeadlessPrint?: (receipt: ReceiptView, printerId: number, selectedJobIds?: readonly string[]) => void;
};

// Summary: Owns the receipt modal's async + state: fetch, printer pick, per-line print, guest-name edit.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-receipt
export function useTransactionReceiptController({
  opened,
  tenantId,
  orderId,
  paymentlineId,
  onClose,
  onHeadlessPrint,
}: ControllerParams) {
  const { employeeState } = useEmployeeSession();
  const employeeSession = employeeState?.employeeSession;

  const offscreenRefs = useRef<Array<HTMLDivElement | null>>([]);
  const printInFlightRef = useRef(false);
  const [receipt, setReceipt] = useState<ReceiptView | null>(null);
  const [loadingReceipt, setLoadingReceipt] = useState(false);
  const [printers, setPrinters] = useState<PrinterView[]>([]);
  const [selectedPrinterId, setSelectedPrinterId] = useState<string | null>(
    () => localStorage.getItem('posReceiptPrinterId') || null,
  );
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    if (!opened || !employeeSession) {
      setReceipt(null);
      return;
    }
    let cancelled = false;
    setLoadingReceipt(true);
    receiptService
      .getReceipt(tenantId, orderId, employeeSession, paymentlineId)
      .then((data) => {
        if (!cancelled) setReceipt(data);
      })
      .catch((err) => {
        if (cancelled) return;
        notifications.show({ color: 'red', title: 'שגיאה', message: 'שגיאה בטעינת הקבלה', autoClose: 3000 });
        console.error('Failed to load receipt:', err);
      })
      .finally(() => {
        if (!cancelled) setLoadingReceipt(false);
      });
    return () => {
      cancelled = true;
    };
  }, [opened, tenantId, orderId, paymentlineId, employeeSession, reloadNonce]);

  useEffect(() => {
    if (!opened || !employeeSession) return;
    setLoadingPrinters(true);
    printersService
      .listActive(tenantId, employeeSession)
      .then((data) => {
        setPrinters(data);
        // Billable (or 0-billable device/sole fallback) wins; else a still-active legacy key.
        const autoId = resolveReceiptAutoPrinterId(data);
        const stored = localStorage.getItem('posReceiptPrinterId');
        if (autoId != null) {
          setSelectedPrinterId(String(autoId));
        } else if (stored && data.some((p) => String(p.id) === stored)) {
          setSelectedPrinterId(stored);
        } else {
          // No fixed/stored pick: default to the first printer so print is never blocked.
          setSelectedPrinterId(data.length > 0 ? String(data[0].id) : null);
        }
      })
      .catch((err) => {
        console.error('Failed to load printers:', err);
        notifications.show({ color: 'red', title: 'שגיאה', message: 'שגיאה בטעינת רשימת המדפסות הפעילות', autoClose: 3000 });
      })
      .finally(() => setLoadingPrinters(false));
  }, [opened, tenantId, employeeSession]);

  // One job per payment line, plus a full "חשבון" summary when paid via >1 payment.
  const printJobs = useMemo(() => (receipt ? receiptsForPrinting(receipt) : []), [receipt]);

  const selectPrinter = useCallback((id: string) => {
    setSelectedPrinterId(id);
    localStorage.setItem('posReceiptPrinterId', id);
  }, []);

  const handlePrint = useCallback(async (printerIdOverride?: string, selectedJobIds?: readonly string[]) => {
    if (printInFlightRef.current) return;
    const printerId = printerIdOverride ?? selectedPrinterId;
    if (!printerId) {
      notifications.show({ color: 'red', title: 'שגיאה', message: 'לא נמצאה מדפסת פעילה במערכת', autoClose: 3000 });
      return;
    }
    if (!employeeSession) {
      notifications.show({ color: 'red', title: 'שגיאה', message: 'שגיאת הרשאה: סשן פקע או לא קיים', autoClose: 3000 });
      return;
    }
    // Fire-and-forget: hand off to the headless queue, toast at dispatch, and close every surface.
    if (onHeadlessPrint) {
      if (!receipt) return;
      if (selectedJobIds) {
        onHeadlessPrint(receipt, Number(printerId), selectedJobIds);
      } else {
        onHeadlessPrint(receipt, Number(printerId));
      }
      notifications.show({ color: 'green', title: 'הדפסה', message: 'הקבלה נשלחה להדפסה', autoClose: 3000 });
      onClose();
      return;
    }
    try {
      printInFlightRef.current = true;
      setIsPrinting(true);

      const selectedJobIdSet = selectedJobIds ? new Set(selectedJobIds) : null;
      const jobsToPrint = selectedJobIdSet ? printJobs.filter((job) => selectedJobIdSet.has(job.id)) : printJobs;
      const jobIds: number[] = [];
      for (const job of jobsToPrint) {
        const jobIndex = printJobs.indexOf(job);
        const node = offscreenRefs.current[jobIndex];
        const image = node ? await captureReceiptSnapshot(node) : undefined;
        if (!image) continue;
        const jobId = await printersService.queueOrderReceiptPrint(
          tenantId,
          orderId,
          Number(printerId),
          image,
          employeeSession,
        );
        jobIds.push(jobId);
      }

      if (jobIds.length === 0) {
        notifications.show({ color: 'red', title: 'שגיאה', message: 'לא ניתן ליצור תמונה להדפסה', autoClose: 3000 });
        return;
      }

      // Toast at dispatch, close every surface, then the queue-poll fires the completion toast.
      notifications.show({ color: 'green', title: 'הדפסה', message: 'הקבלה נשלחה להדפסה', autoClose: 3000 });
      onClose();
      void toastReceiptPrintResults(tenantId, jobIds, employeeSession);
    } catch (err: unknown) {
      console.error('Receipt print failed:', err);
      notifications.show({
        color: 'red',
        title: 'ההדפסה נכשלה',
        message: err instanceof Error ? err.message : 'שגיאה לא ידועה בעת שליחת הקבלה להדפסה',
        autoClose: 3000,
      });
    } finally {
      printInFlightRef.current = false;
      setIsPrinting(false);
    }
  }, [printJobs, selectedPrinterId, employeeSession, tenantId, orderId, onClose, onHeadlessPrint, receipt]);

  // Receipt-class routing: billable supersedes the pin; a 'pick' shows the inline list.
  const receiptTarget = resolveReceiptPrintTarget(printers);
  const needsPrinterChoice = receiptTarget.kind === 'pick';
  // 2+ billable → restrict the inline list to the billable candidates; else the full active list.
  const printerChoices = receiptTarget.kind === 'pick' && receiptTarget.candidates ? receiptTarget.candidates : printers;

  // Billable/fixed/sole → silent; else the inline-list selection (validated).
  // Deliberately NO stored/last-used auto-print (legacy keys hold the old first-printer bug).
  const handlePrintClick = useCallback((selectedJobIds?: readonly string[]) => {
    const target = resolveReceiptPrintTarget(printers);
    if (target.kind === 'print') {
      const val = String(target.printerId);
      selectPrinter(val);
      void handlePrint(val, selectedJobIds);
      return;
    }
    if (selectedPrinterId && printers.some((p) => String(p.id) === selectedPrinterId)) {
      void handlePrint(selectedPrinterId, selectedJobIds);
    }
  }, [printers, selectedPrinterId, selectPrinter, handlePrint]);

  const handleSaveName = useCallback(async () => {
    if (savingName || !employeeSession) return;
    setSavingName(true);
    try {
      await ordersService.updateGuestName(tenantId, orderId, nameDraft.trim() || null, employeeSession);
      setEditingName(false);
      setReloadNonce((nonce) => nonce + 1);
      notifications.show({ color: 'green', title: 'עודכן', message: 'שם הלקוח עודכן', autoClose: 3000 });
    } catch (err: unknown) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: err instanceof Error ? err.message : 'עדכון שם הלקוח נכשל',
        autoClose: 3000,
      });
    } finally {
      setSavingName(false);
    }
  }, [savingName, employeeSession, tenantId, orderId, nameDraft]);

  const startEditName = useCallback(() => {
    setNameDraft(receipt?.guestName ?? '');
    setEditingName(true);
  }, [receipt]);

  const tipAmount = receipt?.totals.tipAmount ?? 0;
  // Keep refund sign so success banner can show negative amounts correctly.
  const baseAmount = receipt ? (receipt.totals.paidAmount - tipAmount) : 0;

  return {
    receipt,
    loadingReceipt,
    printers,
    printerChoices,
    selectedPrinterId,
    selectPrinter,
    loadingPrinters,
    isPrinting,
    printJobs,
    offscreenRefs,
    editingName,
    setEditingName,
    nameDraft,
    setNameDraft,
    savingName,
    handlePrint,
    handlePrintClick,
    needsPrinterChoice,
    handleSaveName,
    startEditName,
    tipAmount,
    baseAmount,
  };
}
